// Qualified-value widget transformers - foundation.presentation Enhancement 12.
//
// A "qualified value" is a scalar whose meaning depends on a qualifier record
// (a unit / currency / timezone / country chosen on a sibling Reference field).
// Each widget family delegates to ONE transformer keyed by `mode`:
//
//   convert (measure)  - value + res.uom        -> "12.5 kg"
//   format  (monetary) - value + res.currency   -> "$4,500.00" / "4.500,00 EUR"
//   reframe (datetime) - instant + res.timezone -> wall-clock in the zone
//   prefix  (phone)    - digits + res.country   -> "+91 98765 43210"
//
// The transform attrs travel on the qualifier M2O record's `data` payload
// (shipped by `display_data_fields`); after an inline pick they may sit at the
// record's top level instead, so `qAttr` checks both. Stored value semantics
// are conservative - `convert`/`prefix` store what the user typed (no magnitude
// rewrite) so adopting the widget never corrupts existing data.

export type QualifierMode = "convert" | "format" | "reframe" | "prefix";

/** A qualifier M2O record as it arrives on the parent record (read or after pick). */
export type QualifierRecord = Record<string, unknown> | null | undefined;

/** Read a transform attr from the qualifier record - `data` payload first, then top level. */
export function qAttr(q: QualifierRecord, name: string): unknown {
    if (!q || typeof q !== "object") return undefined;
    const data = (q as { data?: Record<string, unknown> }).data;
    if (data && data[name] !== undefined && data[name] !== null) return data[name];
    const top = (q as Record<string, unknown>)[name];
    return top === null ? undefined : top;
}

export function toNumber(v: unknown): number | null {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(String(v));
    return Number.isNaN(n) ? null : n;
}

interface NumberFormatOpts {
    decimals?: number | null;
    decimalSeparator?: string;
    thousandSeparator?: string;
}

/** Group-and-decimal formatter. `decimals=null` -> render the number as-is. */
export function formatNumber(n: number, opts: NumberFormatOpts = {}): string {
    const decSep = opts.decimalSeparator || ".";
    const thouSep = opts.thousandSeparator ?? "";
    const negative = n < 0;
    const abs = Math.abs(n);
    let intPart: string;
    let fracPart: string;
    if (opts.decimals === null || opts.decimals === undefined) {
        const raw = String(abs);
        [intPart, fracPart = ""] = raw.split(".");
    } else {
        const fixed = abs.toFixed(opts.decimals);
        [intPart, fracPart = ""] = fixed.split(".");
    }
    if (thouSep) intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
    const body = fracPart ? `${intPart}${decSep}${fracPart}` : intPart;
    return negative ? `-${body}` : body;
}

function escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Strip grouping / symbol / spaces from a typed monetary string -> number. */
export function parseLooseNumber(input: string, decimalSeparator = "."): number | null {
    if (input == null) return null;
    const s0 = String(input).trim();
    if (s0 === "") return null;
    const decSep = decimalSeparator || ".";
    // Keep only digits, sign, and the decimal separator (drops symbol, spaces,
    // and the thousands separator), then normalise the decimal point.
    const keep = new RegExp(`[^0-9\\-${escapeRegExp(decSep)}]`, "g");
    let s = s0.replace(keep, "");
    if (decSep !== ".") s = s.split(decSep).join(".");
    if (s === "" || s === "-" || s === ".") return null;
    const n = parseFloat(s);
    return Number.isNaN(n) ? null : n;
}

// --- Mode resolution --------------------------------------------------------
// The named widget implies the mode (the common path). A future generic widget
// could instead read a `qualifier_mode` attr off the master via `qAttr`.
const WIDGET_MODE: Record<string, QualifierMode> = {
    measure: "convert",
    monetary: "format",
    datetime_tz: "reframe",
    phone: "prefix",
};

export function modeForWidget(widget: string | undefined): QualifierMode | null {
    if (!widget) return null;
    return WIDGET_MODE[widget] ?? null;
}

// --- format (monetary) ------------------------------------------------------

export interface CurrencyDisplay {
    symbol: string;
    position: "before" | "after";
    decimals: number;
    decimalSeparator: string;
    thousandSeparator: string;
}

export function currencyDisplayFrom(q: QualifierRecord): CurrencyDisplay {
    const symbol = (qAttr(q, "symbol") as string) ?? "";
    const positionRaw = String(qAttr(q, "symbol_position") ?? "before").toLowerCase();
    const decimals = toNumber(qAttr(q, "decimal_precision"));
    return {
        symbol,
        position: positionRaw === "after" ? "after" : "before",
        decimals: decimals == null ? 2 : decimals,
        decimalSeparator: (qAttr(q, "decimal_separator") as string) || ".",
        thousandSeparator: (qAttr(q, "thousand_separator") as string) ?? ",",
    };
}

export function formatMonetary(value: unknown, q: QualifierRecord): string {
    const n = toNumber(value);
    if (n == null) return "";
    const c = currencyDisplayFrom(q);
    const num = formatNumber(n, {
        decimals: c.decimals,
        decimalSeparator: c.decimalSeparator,
        thousandSeparator: c.thousandSeparator,
    });
    if (!c.symbol) return num;
    return c.position === "after" ? `${num} ${c.symbol}` : `${c.symbol}${num}`;
}

// --- convert (measure) ------------------------------------------------------

/** Unit label for the dropdown / chip - symbol if present, else code, else "". */
export function unitLabel(q: QualifierRecord): string {
    const sym = qAttr(q, "symbol");
    if (sym) return String(sym);
    const code = qAttr(q, "code");
    if (code) return String(code);
    if (q && typeof q === "object") {
        const dn = (q as Record<string, unknown>).display_name;
        if (dn) return String(dn);
    }
    return "";
}

export function formatMeasure(value: unknown, q: QualifierRecord): string {
    const n = toNumber(value);
    if (n == null) return "";
    const num = formatNumber(n, { decimals: null, thousandSeparator: "," });
    const unit = unitLabel(q);
    return unit ? `${num} ${unit}` : num;
}

// --- reframe (datetime in a timezone) ---------------------------------------

/**
 * Render a stored UTC instant as wall-clock in a zone. The zone comes from the
 * per-record qualifier (`tz_name`) when present, else the supplied fallback
 * (user/session timezone). The stored instant is never mutated.
 */
export function formatZonedDateTime(
    value: unknown,
    q: QualifierRecord,
    fallbackTz?: string,
): string {
    if (value == null || value === "") return "";
    const iso = String(value);
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const tzName = qAttr(q, "tz_name") as string | undefined;
    const tz = tzName || fallbackTz || undefined;
    try {
        const fmt = new Intl.DateTimeFormat("en-GB", {
            timeZone: tz,
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
        const label = fmt.format(d);
        // Show the zone name only when it came from a per-record qualifier.
        return tzName ? `${label} (${tzName})` : label;
    } catch {
        return iso;
    }
}

// --- prefix (phone) ---------------------------------------------------------

export function dialingPrefix(q: QualifierRecord): string {
    const code = qAttr(q, "phone_code");
    if (code == null || code === "") return "";
    return `+${String(code).replace(/^\+/, "")}`;
}

export function formatPhone(value: unknown, q: QualifierRecord): string {
    const digits = value == null ? "" : String(value).trim();
    if (!digits) return "";
    const prefix = dialingPrefix(q);
    if (!prefix) return digits;
    // Avoid doubling the prefix if the stored value already carries it.
    if (digits.startsWith("+")) return digits;
    return `${prefix} ${digits}`;
}
